import _defineProperty from "/jove-quote/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { QuoteFlowModeEnum } from '@jove-frontend/enums';
import camelcaseKeys from 'camelcase-keys';
import dayjs from 'dayjs';
import { isBoolean, isNumber, isObject, uniqueId } from 'lodash';
import { getChubbContractRateSwitchTypeMap } from '../../constants';
import { getTruelyValueMap } from '../../constants/common';
import { PROPERTY_COVER_TYPE_LIST } from '../../constants/coverage';
import { greateAmericanTypeOfOperationCourier } from '../../constants/greateAmerican';
import { ContractEstimatedIncomePerInfoMap, PROVIDER_SOURCE } from '../../constants/quote';
import { QuoteProceessUrlPath } from '../../constants/quotePath';
import { getDeclarationsOptionsMap } from '../../localeConstants';
import { PriceFromEnum, QuoteStatus, YesNoEnum } from '../../shared/generated';
import { getQuoteCurrentInsuranceInfo } from '../../utils/quote';
import { getServerDayjsDateKeepLocalTime } from '../../utils/time';

var appendBasicInfoFromServerData = function appendBasicInfoFromServerData(data, quoteDetail) {
  appendPersonalInfoFromServerData(data, quoteDetail);
  appendDeclarationsFromServerData(data, quoteDetail);
  appendPolicyPeriodDateFromServerData(data, quoteDetail);
  var flowMode = quoteDetail.flowMode,
      billingCycle = quoteDetail.billingCycle,
      specifyTotalPrice = quoteDetail.specifyTotalPrice,
      priceFrom = quoteDetail.priceFrom,
      policyNumber = quoteDetail.policyNumber,
      status = quoteDetail.status,
      providerCode = quoteDetail.providerCode,
      userId = quoteDetail.userId,
      isSimpleFlow = quoteDetail.isSimpleFlow; // Provider code

  data.providerCode = providerCode; // Fast tracker
  // data.flowMode = flowMode;

  data.flowMode = isSimpleFlow ? QuoteFlowModeEnum.fastTrack : QuoteFlowModeEnum.normal; // Payment mode

  data.billingCycle = billingCycle; // Manual set price

  data.specifyTotalPrice = specifyTotalPrice || ''; // Pay plan:

  data.priceFrom = priceFrom === PriceFromEnum.MANUAL ? true : false; // Policy number

  data.policyNumber = policyNumber; // Flag

  data.cannotModifyPrice = [QuoteStatus.PAYMENT_PENDING, QuoteStatus.CANCELLED, QuoteStatus.PURCHASE_FAILED, QuoteStatus.SUBSCRIPTION_PAYMENT_FAILED, QuoteStatus.PURCHASED, QuoteStatus.PAYMENT_PROCESSING, QuoteStatus.DROPOUT_AFTER_SIGNING].includes(status);
  data.coverOptions = {}; // Special handler: Set empty string of some field

  ['businessAddress', 'businessAddress2', 'detailedAddress', 'businessPostcode', 'businessCity', 'businessCounty', 'businessState'].forEach(function (key) {
    if (!quoteDetail[key]) quoteDetail[key] = '';
  });
  data.userId = userId;
};

var appendPersonalInfoFromServerData = function appendPersonalInfoFromServerData(data, quoteDetail) {
  var firstName = quoteDetail.firstName,
      lastName = quoteDetail.lastName,
      email = quoteDetail.email,
      phone = quoteDetail.phone,
      country = quoteDetail.country;
  data.firstName = firstName;
  data.lastName = lastName;
  data.email = email;
  data.phone = !!phone ? phone !== null && phone !== void 0 && phone.startsWith('+') ? phone : "+".concat(phone) : phone;
  data.country = country;
};

var appendPolicyPeriodDateFromServerData = function appendPolicyPeriodDateFromServerData(data, quoteDetail) {
  var projectPeriodStart = quoteDetail.projectPeriodStart,
      updateEffectiveDate = quoteDetail.updateEffectiveDate;
  data.projectPeriodStart = dayjs(!!projectPeriodStart ? getServerDayjsDateKeepLocalTime(projectPeriodStart) : new Date()).toDate();
  data.updateEffectiveDate = !!updateEffectiveDate ? dayjs(updateEffectiveDate).toDate() : dayjs().add(1, 'day').toDate();
};

var appendBusinessTypeFromServerData = function appendBusinessTypeFromServerData(data, quoteDetail) {
  var businessType = quoteDetail.businessType;
  data.businessType = businessType;
};

export var appendCompanyAndAddressFromServerData = function appendCompanyAndAddressFromServerData(data, quoteDetail) {
  var businessCountry = quoteDetail.businessCountry,
      companyName = quoteDetail.companyName,
      businessAddress = quoteDetail.businessAddress,
      businessAddress2 = quoteDetail.businessAddress2,
      streetNumber = quoteDetail.streetNumber,
      businessPostcode = quoteDetail.businessPostcode,
      businessCity = quoteDetail.businessCity,
      businessCounty = quoteDetail.businessCounty,
      businessState = quoteDetail.businessState,
      detailedAddress = quoteDetail.detailedAddress,
      companyIncorporationDate = quoteDetail.companyIncorporationDate,
      registrationNumber = quoteDetail.registrationNumber,
      taxNumber = quoteDetail.taxNumber;
  data.businessCountry = businessCountry || '';
  data.companyName = companyName || '';
  data.businessAddress = businessAddress || '';
  data.streetNumber = streetNumber || '';
  data.businessPostcode = businessPostcode || '';
  data.businessCity = businessCity || '';
  data.businessCounty = businessCounty || '';
  data.businessState = businessState || '';

  if ([PROVIDER_SOURCE().GREATEAMERICAN.value, PROVIDER_SOURCE().GREATEAMERICAN_BUSINESS_INSURANCE.value].includes(quoteDetail.providerCode)) {
    data.detailedAddress = businessAddress2;
    data.addressInfo = {};
  } else {
    data.businessAddress2 = detailedAddress;
    data.detailedAddress = detailedAddress;

    try {
      data.companyIncorporationDate = !!companyIncorporationDate ? new Date(companyIncorporationDate) : null;
    } catch (_) {}

    data.registrationNumber = registrationNumber;
    data.taxNumber = taxNumber;
  }
};

var appendBusinessActivityFromServerData = function appendBusinessActivityFromServerData(data, quoteDetail) {
  var businessActivity = quoteDetail.businessActivity,
      customizedBusinessActivity = quoteDetail.customizedBusinessActivity,
      r2ROption = quoteDetail.r2ROption,
      propertyActivities = quoteDetail.propertyActivities,
      customizedActivityDescription = quoteDetail.customizedActivityDescription;
  data.businessActivity = !!customizedBusinessActivity ? 'other' : businessActivity;
  data.customizedBusinessActivity = customizedBusinessActivity;
  data.r2ROption = r2ROption;
  data.propertyActivities = propertyActivities || [];
  data.customizedActivityDescription = customizedActivityDescription || '';
};

var appendPolicyOfTypeFromServerData = function appendPolicyOfTypeFromServerData(data, quoteDetail) {
  var policyType = quoteDetail.policyType;
  data.policyType = policyType;
};

var appendEstimatedTurnoverFromServerData = function appendEstimatedTurnoverFromServerData(data, quoteDetail) {
  var turnover = quoteDetail.turnover,
      turnoverRaw = quoteDetail.turnoverRaw;
  data.turnover = turnover;
  data.turnoverRaw = turnoverRaw;
};

var appendErnFromServerData = function appendErnFromServerData(data, quoteDetail) {
  var ern = quoteDetail.ern,
      ernStatus = quoteDetail.ernStatus;
  data.ern = ern;
  data.ernExempt = ernStatus === 'exempt';
};

var appendRetroactiveDateFromServerData = function appendRetroactiveDateFromServerData(data, quoteDetail) {
  var retroactiveDate = quoteDetail.retroactiveDate;
  data.retroactiveDate = !!retroactiveDate ? dayjs(retroactiveDate).toDate() : null;
};

var appendPausingInsuranceFromServerData = function appendPausingInsuranceFromServerData(data, quoteDetail) {
  var pausingInsurance = quoteDetail.pausingInsurance;
  data.pausingInsurance = pausingInsurance;
};

var appendClientNameFromServerData = function appendClientNameFromServerData(data, quoteDetail) {
  var clientName = quoteDetail.clientName;
  data.clientName = clientName;
};

var appendContractPeriodFromServerData = function appendContractPeriodFromServerData(data, quoteDetail) {
  var contractAddMonths = quoteDetail.contractAddMonths,
      projectPeriodStart = quoteDetail.projectPeriodStart,
      projectPeriodEnd = quoteDetail.projectPeriodEnd;
  data.contractAddMonths = contractAddMonths;
  data.projectPeriodEnd = !!projectPeriodEnd ? dayjs(projectPeriodEnd).toDate() : null;
};

var appendRateAndTotalEstimatedIncomeFromServerData = function appendRateAndTotalEstimatedIncomeFromServerData(data, quoteDetail) {
  var _ContractEstimatedInc;

  var rateAndTotal = quoteDetail.rateAndTotal;
  data.rateAndTotal = rateAndTotal || {
    incomeType: getChubbContractRateSwitchTypeMap().rate.value,
    income: 0,
    rate: 150,
    perType: (_ContractEstimatedInc = ContractEstimatedIncomePerInfoMap['perDay']) === null || _ContractEstimatedInc === void 0 ? void 0 : _ContractEstimatedInc.value,
    perCount: 3
  };
};

var appendEmployeesCompositionFromServerData = function appendEmployeesCompositionFromServerData(data, quoteDetail) {
  var _elSize$manualEmploye, _elSize$clericalEmplo;

  var elSize = quoteDetail.elSize,
      employees = quoteDetail.employees;
  data.elSize = {
    manualEmployees: (_elSize$manualEmploye = elSize === null || elSize === void 0 ? void 0 : elSize.manualEmployees) !== null && _elSize$manualEmploye !== void 0 ? _elSize$manualEmploye : null,
    clericalEmployees: (_elSize$clericalEmplo = elSize === null || elSize === void 0 ? void 0 : elSize.clericalEmployees) !== null && _elSize$clericalEmplo !== void 0 ? _elSize$clericalEmplo : null
  };
  data.employees = employees;
};

var appendIncomeSplitFromServerData = function appendIncomeSplitFromServerData(data, quoteDetail) {
  var incomeOrigin = quoteDetail.incomeOrigin,
      extendedGeographicCoverage = quoteDetail.extendedGeographicCoverage;
  data.incomeOrigin = incomeOrigin;
  data.extendedGeographicCoverage = extendedGeographicCoverage;
};

var appendCoversFromServerData = function appendCoversFromServerData(data, quoteDetail) {
  var covers = quoteDetail.covers,
      turnOnCombinedPackage = quoteDetail.turnOnCombinedPackage;

  var newCovers = _objectSpread({}, covers);

  if (!!(covers !== null && covers !== void 0 && covers[PROPERTY_COVER_TYPE_LIST])) {
    newCovers[PROPERTY_COVER_TYPE_LIST] = isNumber(covers === null || covers === void 0 ? void 0 : covers[PROPERTY_COVER_TYPE_LIST]) ? [] : covers === null || covers === void 0 ? void 0 : covers[PROPERTY_COVER_TYPE_LIST];
  }

  data.covers = newCovers;
  data.turnOnCombinedPackage = turnOnCombinedPackage;
};

var appendDeclarationsFromServerData = function appendDeclarationsFromServerData(data, quoteDetail) {
  var declarationQuestions = quoteDetail.declarationQuestions,
      declarationComment = quoteDetail.declarationComment;
  data.declarationQuestions = declarationQuestions;
  data.declarationComment = declarationComment;
};

var appendPersonalDataFromServerData = function appendPersonalDataFromServerData(data, quoteDetail) {
  var birthDate = quoteDetail.birthDate;
  data.birthDate = birthDate;
};

var appendCoverTypeFromServerData = function appendCoverTypeFromServerData(data, quoteDetail) {
  var coverType = quoteDetail.coverType;
  data.coverType = coverType;
};

var appendClaimsFromServerData = function appendClaimsFromServerData(data, quoteDetail) {
  var hasClaims = quoteDetail.hasClaims,
      claimList = quoteDetail.claimList;
  data.hasClaims = hasClaims;
  data.claimList = claimList || [];
};

var appendRecruitemExtraDataFromServerData = function appendRecruitemExtraDataFromServerData(data, quoteDetail) {
  var recruitmentSplitPercent = quoteDetail.recruitmentSplitPercent,
      payrollSplitPercent = quoteDetail.payrollSplitPercent,
      recruitmentBusinessType = quoteDetail.recruitmentBusinessType,
      payrollForPlacedCandidate = quoteDetail.payrollForPlacedCandidate,
      permanentSplitPercentForPlacedCandidate = quoteDetail.permanentSplitPercentForPlacedCandidate,
      temporarySplitPercentForPlacedCandidate = quoteDetail.temporarySplitPercentForPlacedCandidate,
      placementTypeAndSplitList = quoteDetail.placementTypeAndSplitList;
  data.recruitmentBusinessType = recruitmentBusinessType;
  data.recruitmentSplitPercent = recruitmentSplitPercent;
  data.payrollSplitPercent = payrollSplitPercent;
  data.payrollForPlacedCandidate = payrollForPlacedCandidate;
  data.permanentSplitPercentForPlacedCandidate = permanentSplitPercentForPlacedCandidate;
  data.temporarySplitPercentForPlacedCandidate = temporarySplitPercentForPlacedCandidate;
  data.placementTypeAndSplitList = (placementTypeAndSplitList || []).map(function (item) {
    return {
      value: item.placement_type,
      label: item.label || item.placement_type,
      percent: item.value
    };
  });
};

var appendYearsInBusinessFromServerData = function appendYearsInBusinessFromServerData(data, quoteDetail) {
  var yearsInBusiness = quoteDetail.yearsInBusiness;
  data.yearsInBusiness = yearsInBusiness;
};

var appendVehicleListFromServerData = function appendVehicleListFromServerData(data, quoteDetail) {
  var vehicleList = quoteDetail.vehicleList;
  data.vehicleList = (vehicleList || []).map(function (item) {
    return _objectSpread(_objectSpread({}, item), {}, {
      registrationNumber: item.registration_number
    });
  });
};

var appendAccountancyActivitiesFromServerData = function appendAccountancyActivitiesFromServerData(data, quoteDetail) {
  var activitySplit = quoteDetail.activitySplit;
  data.accountancyActivityList = Object.keys(activitySplit || {}).map(function (key) {
    return {
      value: key,
      label: key,
      count: activitySplit[key]
    };
  });
};

var appendProfessionalAssociationsMemberFromServerData = function appendProfessionalAssociationsMemberFromServerData(data, quoteDetail) {
  var membership = quoteDetail.membership;
  data.professionalAssociationsMember = membership || '';
};

var appendInsuranceBundleFromServerData = function appendInsuranceBundleFromServerData(data, quoteDetail) {
  var insuranceBundle = quoteDetail.insuranceBundle;
  data.insuranceBundle = insuranceBundle;
};

var appendCoverageConfigurationFromServerData = function appendCoverageConfigurationFromServerData(data, quoteDetail) {
  var coverageConfiguration = quoteDetail.coverageConfiguration;
  data.coverageConfiguration = coverageConfiguration;
};

var appendHearAboutInfoFromServerData = function appendHearAboutInfoFromServerData(data, quoteDetail) {
  var hearAboutSource = quoteDetail.hearAboutSource,
      hearAboutCustomInput = quoteDetail.hearAboutCustomInput;
  data.hearAboutSource = hearAboutSource;
  data.hearAboutCustomInput = hearAboutCustomInput;
};

var appendFirstPaymentDelayedInceptionDate = function appendFirstPaymentDelayedInceptionDate(data, quoteDetail) {
  var firstPaymentDelayedInceptionDate = quoteDetail.firstPaymentDelayedInceptionDate;
  data.firstPaymentDelayedInceptionDate = !!firstPaymentDelayedInceptionDate;
};

var appendAssumptionInfo = function appendAssumptionInfo(data, quoteDetail) {
  var assumptionFields = quoteDetail.assumptionFields;
  data.assumptionFields = isObject(assumptionFields) ? camelcaseKeys(assumptionFields) : {};
};

var appendCustomInputCoverages = function appendCustomInputCoverages(data, quoteDetail) {
  var customizedCovers = quoteDetail.customizedCovers;
  var list = [];
  Object.keys(customizedCovers || {}).forEach(function (key) {
    list.push({
      id: uniqueId(),
      name: key,
      coverage: customizedCovers[key]
    });
  });
  data.customizedCovers = list;
};

var appendTimeFieldsFromServerData = function appendTimeFieldsFromServerData(data, quoteDetail) {
  ['professionalLiabilityRetroactiveDate', 'cyberRetroactiveDate', 'mediaLiabilityRetroactiveDate'].forEach(function (key) {
    data[key] = !!quoteDetail[key] ? dayjs(getServerDayjsDateKeepLocalTime(quoteDetail[key])).toDate() : null;
  });
};

var convertChubbServerQuoteDetailDataToLocalStructureData = function convertChubbServerQuoteDetailDataToLocalStructureData(quoteDetail) {
  var data = {};
  var technologyIndustry = quoteDetail.technologyIndustry,
      undertakenWork = quoteDetail.undertakenWork,
      involvedActivities = quoteDetail.involvedActivities,
      contract1M = quoteDetail.contract1M;
  appendBasicInfoFromServerData(data, quoteDetail); // QuoteProcess_BusinessType

  appendBusinessTypeFromServerData(data, quoteDetail); // * QuoteProcess_CompanyAddress

  appendCompanyAndAddressFromServerData(data, quoteDetail); // QuoteProcess_BusinessActivities

  appendBusinessActivityFromServerData(data, quoteDetail); // QuoteProcess_ChubbTechIndustry

  data.technologyIndustry = technologyIndustry; // QuoteProcess_AdditionalActivities

  data.undertakenWork = undertakenWork;
  data.involvedActivities = involvedActivities || []; // QuoteProcess_TypeOfPolicy

  appendPolicyOfTypeFromServerData(data, quoteDetail); // QuoteProcess_ChubbContractDetails

  data.contract1M = contract1M; // QuoteProcess_EstimatedTurnover

  appendEstimatedTurnoverFromServerData(data, quoteDetail); // QuoteProcess_EmployerReferenceNumber

  appendErnFromServerData(data, quoteDetail); // QuoteProcess_RetroactiveDate

  appendRetroactiveDateFromServerData(data, quoteDetail); // QuoteProcess_PausingInsurance

  appendPausingInsuranceFromServerData(data, quoteDetail); // QuoteProcess_ClientName

  appendClientNameFromServerData(data, quoteDetail); // QuoteProcess_ContractPeriod

  appendContractPeriodFromServerData(data, quoteDetail); // QuoteProcess_RateAndTotalEstimatedIncome

  appendRateAndTotalEstimatedIncomeFromServerData(data, quoteDetail); // QuoteProcess_EmployeesComposition

  appendEmployeesCompositionFromServerData(data, quoteDetail); // QuoteProcess_IncomeSplit

  appendIncomeSplitFromServerData(data, quoteDetail); // QuoteProcess_CustomizeCover

  appendCoversFromServerData(data, quoteDetail);
  return data;
};

var convertGreatAmericanServerQuoteDetailDataToLocalStructureData = function convertGreatAmericanServerQuoteDetailDataToLocalStructureData(quoteDetail) {
  var data = {};
  appendBasicInfoFromServerData(data, quoteDetail); // QuoteProcess_DotAsk

  data.haveDotNumber = !quoteDetail.haveNoDotNumber;
  data.haveNoDotNumber = !!quoteDetail.haveNoDotNumber; // QuoteProcess_DotNumber

  data.dotNumber = quoteDetail.dotNumber; // QuoteProcess_GreateAmericanFastProcessCriteria

  if (isBoolean(quoteDetail.isSimpleFlow)) {
    data.declarationQuestions = _objectSpread(_objectSpread({}, quoteDetail.declarationQuestions), {}, {
      great_american_fast_process_criteria: getDeclarationsOptionsMap()['1'].find(function (item) {
        return item.truelyValue === quoteDetail.isSimpleFlow;
      })
    });
  } // QuoteProcess_TypeOfRemovalJob


  data.typeOfOperation = quoteDetail.typeOfOperation;
  data.typeOfOperation = quoteDetail.typeOfOperation === 'dryVan' ? greateAmericanTypeOfOperationCourier : quoteDetail.typeOfOperation; // QuoteProcess_GreateAmericanEnterCustomerHouse: enter_customer_house, enter_customer_house_carry_heavy_items
  // QuoteProcess_ExperienceCarryMixedGoods: greate_american_experience_carry_mixed_goods
  // QuoteProcess_UseWhatCar: greate_american_use_what_car
  // QuoteProcess_MixedCourierWork: greate_american_mixed_courier_work
  // QuoteProcess_TypeOfCommodities + QuoteProcess_PercentageHauledCommodity

  data.typeOfCommodities = quoteDetail.typeOfCommodities;
  data.commodityPercentage = quoteDetail.commodityPercentage;
  data.commodityList = (quoteDetail.commodityList || []).map(function (item) {
    return {
      value: item.option_id,
      label: item.option_id,
      count: item.value
    };
  }); // QuoteProcess_AverageRadiusOperation

  data.averageRadiusOfOperation = quoteDetail.averageRadiusOfOperation; // QuoteProcess_Subhauler: greate_american_subhauler, greate_american_subhauler_indemnify
  // QuoteProcess_CargoLoses: greate_american_cargo_loses
  // QuoteProcess_NumberOfLosses

  data.numberLossesLast3Years = quoteDetail.numberLossesLast3Years;
  data.totalLossesPaidLast3Years = quoteDetail.totalLossesPaidLast3Years;
  data.noAnyCargoLoss = quoteDetail.noAnyCargoLoss; // QuoteProcess_BusinessType

  data.businessType = quoteDetail.businessType; // QuoteProcess_CompanyAddress

  appendCompanyAndAddressFromServerData(data, quoteDetail); // QuoteProcess_YearsOfExperience

  appendYearsInBusinessFromServerData(data, quoteDetail); // QuoteProcess_NumberOfDrivers

  data.numberOfDrivers = quoteDetail.numberOfDrivers; // QuoteProcess_DriverAgeRange: greate_american_drivers_age_range, greate_american_drivers_age_range_acceptable_dot
  // QuoteProcess_NumberOfMinorViolation

  data.minorViolations = quoteDetail.minorViolations; // QuoteProcess_US_Vehicles

  appendVehicleListFromServerData(data, quoteDetail); // QuoteProcess_AcceptanceCriteria: greate_american_acceptance_criteria, greate_american_acceptance_criteria_different_name

  var acceptanceCriteriaSelectCheckedList = []; // acceptanceCriteriaInsurance is 'I disagree' here.

  if (quoteDetail.coverageCancelledOrNonRenewedInLast5Years === YesNoEnum.NO) {
    acceptanceCriteriaSelectCheckedList.push('coverage_cancelled_or_non_renewed_in_last_5_years');
  }

  if (quoteDetail.currentCarrier === YesNoEnum.NO) {
    acceptanceCriteriaSelectCheckedList.push('current_carrier');
  }

  data.acceptanceCriteriaSelectCheckedList = acceptanceCriteriaSelectCheckedList;
  data.dotNumberPriorAuthority = quoteDetail.dotNumberPriorAuthority; // QuoteProcess_UserConfirmReceived: user_confirm_received
  // QuoteProcess_MotorTruckCargoCover + QuoteProcess_WhatIncludedMtcInsurance + QuoteProcess_MtcOptionalCoverage / QuoteProcess_GreateAmericanFastProcessCovers

  appendCoversFromServerData(data, quoteDetail); // QuoteProcess_RefrigeratedEquipment: greate_american_refrigerated_equipment
  // QuoteProcess_RefrigirationYearExperience: greate_american_refrigiration_year_experience
  // QuoteProcess_RiggingTransportingGoods: greate_american_rigging_transporting_goods

  return data;
};

var convertAccelerantServerQuoteDetailDataToLocalStructureData = function convertAccelerantServerQuoteDetailDataToLocalStructureData(quoteDetail) {
  var data = {};
  appendBasicInfoFromServerData(data, quoteDetail); // QuoteProcess_BusinessType

  appendBusinessTypeFromServerData(data, quoteDetail); // QuoteProcess_BusinessActivities

  appendBusinessActivityFromServerData(data, quoteDetail); // QuoteProcess_TypeOfPolicy

  appendPolicyOfTypeFromServerData(data, quoteDetail); // QuoteProcess_EstimatedTurnover

  appendEstimatedTurnoverFromServerData(data, quoteDetail); // QuoteProcess_IncomeSplit

  appendIncomeSplitFromServerData(data, quoteDetail); // QuoteProcess_CustomizeCover

  appendCoversFromServerData(data, quoteDetail); // QuoteProcess_EmployeesComposition

  appendEmployeesCompositionFromServerData(data, quoteDetail); // QuoteProcess_RetroactiveDate

  appendRetroactiveDateFromServerData(data, quoteDetail); // AccountancyActivities

  appendAccountancyActivitiesFromServerData(data, quoteDetail); // Professional associations member

  appendProfessionalAssociationsMemberFromServerData(data, quoteDetail);
  return data;
};

var convertRecruitmentServerQuoteDetailDataToLocalStructureData = function convertRecruitmentServerQuoteDetailDataToLocalStructureData(quoteDetail) {
  QuoteProceessUrlPath;
  var data = {};
  appendBasicInfoFromServerData(data, quoteDetail); // QuoteProcess_BusinessType

  appendBusinessTypeFromServerData(data, quoteDetail); // QuoteProcess_CompanyAddress

  appendCompanyAndAddressFromServerData(data, quoteDetail); // QuoteProcess_PersonalData

  appendPersonalDataFromServerData(data, quoteDetail); // QuoteProcess_EstimatedTurnover

  appendEstimatedTurnoverFromServerData(data, quoteDetail); // QuoteProcess_IncomeSplit

  appendIncomeSplitFromServerData(data, quoteDetail); // QuoteProcess_Employees

  appendEmployeesCompositionFromServerData(data, quoteDetail); // QuoteProcess_EmployerReferenceNumber

  appendErnFromServerData(data, quoteDetail); // QuoteProcess_CoverType

  appendCoverTypeFromServerData(data, quoteDetail); // QuoteProcess_CustomizeCover

  appendCoversFromServerData(data, quoteDetail); // QuoteProcess_Claims

  appendClaimsFromServerData(data, quoteDetail); // QuoteProcess_RetroactiveDate

  appendRetroactiveDateFromServerData(data, quoteDetail);
  appendRecruitemExtraDataFromServerData(data, quoteDetail);
  return data;
};

var convertGreatAmericanBusinessServerQuoteDetailDataToLocalStructureData = function convertGreatAmericanBusinessServerQuoteDetailDataToLocalStructureData(quoteDetail) {
  var data = {};
  appendBasicInfoFromServerData(data, quoteDetail);

  if (isBoolean(quoteDetail.isSimpleFlow)) {
    data.declarationQuestions = _objectSpread(_objectSpread({}, quoteDetail.declarationQuestions), {}, {
      greate_american_us_quick_journey_criteria: getDeclarationsOptionsMap()['1'].find(function (item) {
        return item.truelyValue === quoteDetail.isSimpleFlow;
      })
    });
  }

  appendBusinessTypeFromServerData(data, quoteDetail);
  appendBusinessActivityFromServerData(data, quoteDetail);
  appendEstimatedTurnoverFromServerData(data, quoteDetail);
  appendCompanyAndAddressFromServerData(data, quoteDetail);
  appendRetroactiveDateFromServerData(data, quoteDetail);
  appendYearsInBusinessFromServerData(data, quoteDetail);
  appendCoversFromServerData(data, quoteDetail);
  data = _objectSpread(_objectSpread({}, data), {}, {
    staffExperienceYears: quoteDetail.staffExperienceYears || '',
    workRequireCertifiedPercentage: quoteDetail.workRequireCertifiedPercentage || '',
    writtenContractPercentage: quoteDetail.writtenContractPercentage || '',
    subcontractorPercentage: quoteDetail.subcontractorPercentage || '',
    totalCostClaims: quoteDetail.totalCostClaims,
    totalInsurancePremiumPaid: quoteDetail.totalInsurancePremiumPaid,
    extraDeclarationSelectOptionList: quoteDetail.extraDeclarationSelectOptionList || [],
    extraDeclarationComment: quoteDetail.extraDeclarationComment || ''
  });
  return data;
};

var convertAccelerantGitServerQuoteDetailDataToLocalStructureData = function convertAccelerantGitServerQuoteDetailDataToLocalStructureData(quoteDetail) {
  var data = {};
  appendBasicInfoFromServerData(data, quoteDetail); // QuoteProcess_BusinessType

  appendBusinessTypeFromServerData(data, quoteDetail); // QuoteProcess_CompanyAddress

  appendCompanyAndAddressFromServerData(data, quoteDetail); // QuoteProcess_PersonalData

  appendPersonalDataFromServerData(data, quoteDetail);
  appendBusinessActivityFromServerData(data, quoteDetail);
  appendEmployeesCompositionFromServerData(data, quoteDetail); // Insurance bundle

  appendInsuranceBundleFromServerData(data, quoteDetail);
  appendVehicleListFromServerData(data, quoteDetail);
  appendCoversFromServerData(data, quoteDetail);
  return data;
};

var convertGreatAmericanBusinessSpecialServerQuoteDetailDataToLocalStructureData = function convertGreatAmericanBusinessSpecialServerQuoteDetailDataToLocalStructureData(data, quoteDetail) {
  if (isBoolean(quoteDetail.isSimpleFlow)) {
    data.declarationQuestions = _objectSpread(_objectSpread({}, quoteDetail.declarationQuestions), {}, {
      greate_american_us_quick_journey_criteria: getDeclarationsOptionsMap()['1'].find(function (item) {
        return item.truelyValue === quoteDetail.isSimpleFlow;
      })
    });
  }

  var staffExperienceYears = quoteDetail.staffExperienceYears,
      workRequireCertifiedPercentage = quoteDetail.workRequireCertifiedPercentage,
      writtenContractPercentage = quoteDetail.writtenContractPercentage,
      subcontractorPercentage = quoteDetail.subcontractorPercentage,
      totalCostClaims = quoteDetail.totalCostClaims,
      totalInsurancePremiumPaid = quoteDetail.totalInsurancePremiumPaid,
      extraDeclarationSelectOptionList = quoteDetail.extraDeclarationSelectOptionList,
      extraDeclarationComment = quoteDetail.extraDeclarationComment;
  data.staffExperienceYears = staffExperienceYears || '';
  data.workRequireCertifiedPercentage = workRequireCertifiedPercentage || '';
  data.writtenContractPercentage = writtenContractPercentage || '';
  data.subcontractorPercentage = subcontractorPercentage || '';
  data.totalCostClaims = totalCostClaims;
  data.totalInsurancePremiumPaid = totalInsurancePremiumPaid;
  data.extraDeclarationSelectOptionList = extraDeclarationSelectOptionList || [];
  data.extraDeclarationComment = extraDeclarationComment || '';
};

var convertRecruitmentSpecialServerQuoteDetailDataToLocalStructureData = function convertRecruitmentSpecialServerQuoteDetailDataToLocalStructureData(data, quoteDetail) {
  var recruitmentSplitPercent = quoteDetail.recruitmentSplitPercent,
      payrollSplitPercent = quoteDetail.payrollSplitPercent,
      recruitmentBusinessType = quoteDetail.recruitmentBusinessType,
      payrollForPlacedCandidate = quoteDetail.payrollForPlacedCandidate,
      permanentSplitPercentForPlacedCandidate = quoteDetail.permanentSplitPercentForPlacedCandidate,
      temporarySplitPercentForPlacedCandidate = quoteDetail.temporarySplitPercentForPlacedCandidate,
      placementTypeAndSplitList = quoteDetail.placementTypeAndSplitList;
  data.recruitmentBusinessType = recruitmentBusinessType;
  data.recruitmentSplitPercent = recruitmentSplitPercent;
  data.payrollSplitPercent = payrollSplitPercent;
  data.payrollForPlacedCandidate = payrollForPlacedCandidate;
  data.permanentSplitPercentForPlacedCandidate = permanentSplitPercentForPlacedCandidate;
  data.temporarySplitPercentForPlacedCandidate = temporarySplitPercentForPlacedCandidate;
  data.placementTypeAndSplitList = (placementTypeAndSplitList || []).map(function (item) {
    return {
      value: item.placement_type,
      label: item.label || item.placement_type,
      percent: item.value
    };
  });
};

var convertGreatAmericanSpecialServerQuoteDetailDataToLocalStructureData = function convertGreatAmericanSpecialServerQuoteDetailDataToLocalStructureData(data, quoteDetail) {
  var _ref = quoteDetail || {},
      haveNoDotNumber = _ref.haveNoDotNumber,
      dotNumber = _ref.dotNumber,
      isSimpleFlow = _ref.isSimpleFlow,
      declarationQuestions = _ref.declarationQuestions,
      typeOfOperation = _ref.typeOfOperation,
      typeOfCommodities = _ref.typeOfCommodities,
      commodityPercentage = _ref.commodityPercentage,
      commodityList = _ref.commodityList,
      averageRadiusOfOperation = _ref.averageRadiusOfOperation,
      numberLossesLast3Years = _ref.numberLossesLast3Years,
      totalLossesPaidLast3Years = _ref.totalLossesPaidLast3Years,
      noAnyCargoLoss = _ref.noAnyCargoLoss,
      numberOfDrivers = _ref.numberOfDrivers,
      coverageCancelledOrNonRenewedInLast5Years = _ref.coverageCancelledOrNonRenewedInLast5Years,
      currentCarrier = _ref.currentCarrier,
      dotNumberPriorAuthority = _ref.dotNumberPriorAuthority; // QuoteProcess_DotAsk


  data.haveDotNumber = !haveNoDotNumber;
  data.haveNoDotNumber = !!haveNoDotNumber; // QuoteProcess_DotNumber

  data.dotNumber = dotNumber; // QuoteProcess_GreateAmericanFastProcessCriteria

  if (isBoolean(isSimpleFlow)) {
    data.declarationQuestions = _objectSpread(_objectSpread({}, declarationQuestions), {}, {
      great_american_fast_process_criteria: getDeclarationsOptionsMap()['1'].find(function (item) {
        return item.truelyValue === isSimpleFlow;
      })
    });
  } // QuoteProcess_TypeOfRemovalJob


  data.typeOfOperation = typeOfOperation === 'dryVan' ? greateAmericanTypeOfOperationCourier : typeOfOperation; // QuoteProcess_GreateAmericanEnterCustomerHouse: enter_customer_house, enter_customer_house_carry_heavy_items
  // QuoteProcess_ExperienceCarryMixedGoods: greate_american_experience_carry_mixed_goods
  // QuoteProcess_UseWhatCar: greate_american_use_what_car
  // QuoteProcess_MixedCourierWork: greate_american_mixed_courier_work
  // QuoteProcess_TypeOfCommodities + QuoteProcess_PercentageHauledCommodity

  data.typeOfCommodities = typeOfCommodities;
  data.commodityPercentage = commodityPercentage;
  data.commodityList = (commodityList || []).map(function (item) {
    return {
      value: item.option_id,
      label: item.option_id,
      count: item.value
    };
  }); // QuoteProcess_AverageRadiusOperation

  data.averageRadiusOfOperation = averageRadiusOfOperation; // QuoteProcess_Subhauler: greate_american_subhauler, greate_american_subhauler_indemnify
  // QuoteProcess_CargoLoses: greate_american_cargo_loses
  // QuoteProcess_NumberOfLosses

  data.numberLossesLast3Years = numberLossesLast3Years;
  data.totalLossesPaidLast3Years = totalLossesPaidLast3Years;
  data.noAnyCargoLoss = noAnyCargoLoss; // QuoteProcess_NumberOfDrivers

  data.numberOfDrivers = numberOfDrivers; // QuoteProcess_DriverAgeRange: greate_american_drivers_age_range, greate_american_drivers_age_range_acceptable_dot
  // QuoteProcess_NumberOfMinorViolation

  data.minorViolations = quoteDetail.minorViolations; // QuoteProcess_AcceptanceCriteria: greate_american_acceptance_criteria, greate_american_acceptance_criteria_different_name

  var acceptanceCriteriaSelectCheckedList = []; // acceptanceCriteriaInsurance is 'I disagree' here.

  if (coverageCancelledOrNonRenewedInLast5Years === YesNoEnum.NO) {
    acceptanceCriteriaSelectCheckedList.push('coverage_cancelled_or_non_renewed_in_last_5_years');
  }

  if (currentCarrier === YesNoEnum.NO) {
    acceptanceCriteriaSelectCheckedList.push('current_carrier');
  }

  data.acceptanceCriteriaSelectCheckedList = acceptanceCriteriaSelectCheckedList;
  data.dotNumberPriorAuthority = dotNumberPriorAuthority;
};

var convertHiscoxUSSpecialServerQuoteDetailDataToLocalStructureData = function convertHiscoxUSSpecialServerQuoteDetailDataToLocalStructureData(data, quoteDetail) {
  var estSalesLargestCustmr1 = quoteDetail.estSalesLargestCustmr1,
      itConsultingTechnologyServicesOptionList = quoteDetail.itConsultingTechnologyServicesOptionList;
  data.staffExperienceYears = estSalesLargestCustmr1 || '';
  data.extraDeclarationSelectOptionList = itConsultingTechnologyServicesOptionList || [];
  ['haveProfLiabilityInsurance', 'professionalLiabilityRetroactiveDateAvailable', 'haveCyberInsurance', 'cyberRetroactiveDateAvailable', 'mediaLiabilityRetroactiveDateAvailable'].forEach(function (key) {
    data[key] = isBoolean(quoteDetail[key]) ? quoteDetail[key] ? getTruelyValueMap().yes.value : getTruelyValueMap().no.value : null;
  });
};

var convertAccelerantSpecialServerQuoteDetailDataToLocalStructureData = function convertAccelerantSpecialServerQuoteDetailDataToLocalStructureData(data, quoteDetail) {
  var otherAccountancyActivityDesc = quoteDetail.otherAccountancyActivityDesc;
  data.accountancyOtherActivity = otherAccountancyActivityDesc;
};

var convertInsuranceServerQuoteDetailDataToLocalStructureData = function convertInsuranceServerQuoteDetailDataToLocalStructureData(quoteDetail) {
  var data = {}; // Basic information

  appendBasicInfoFromServerData(data, quoteDetail); // QuoteProcess_BusinessType

  appendBusinessTypeFromServerData(data, quoteDetail); // QuoteProcess_CompanyAddress

  appendCompanyAndAddressFromServerData(data, quoteDetail); // QuoteProcess_PersonalData

  appendPersonalDataFromServerData(data, quoteDetail); // Business activity

  appendBusinessActivityFromServerData(data, quoteDetail); // QuoteProcess_TypeOfPolicy

  appendPolicyOfTypeFromServerData(data, quoteDetail); // Employees composition

  appendEmployeesCompositionFromServerData(data, quoteDetail); // Insurance bundle

  appendInsuranceBundleFromServerData(data, quoteDetail); // Vehicle list

  appendVehicleListFromServerData(data, quoteDetail); // Covers

  appendCoversFromServerData(data, quoteDetail); // Estimated turnover

  appendEstimatedTurnoverFromServerData(data, quoteDetail); // Retroactive date

  appendRetroactiveDateFromServerData(data, quoteDetail); // Years in business

  appendYearsInBusinessFromServerData(data, quoteDetail); // QuoteProcess_IncomeSplit

  appendIncomeSplitFromServerData(data, quoteDetail); // QuoteProcess_EmployerReferenceNumber

  appendErnFromServerData(data, quoteDetail); // QuoteProcess_CoverType

  appendCoverTypeFromServerData(data, quoteDetail); // QuoteProcess_Claims

  appendClaimsFromServerData(data, quoteDetail); // AccountancyActivities

  appendAccountancyActivitiesFromServerData(data, quoteDetail); // Professional associations member

  appendProfessionalAssociationsMemberFromServerData(data, quoteDetail); // Coverage Configuration

  appendCoverageConfigurationFromServerData(data, quoteDetail); // Hear about us

  appendHearAboutInfoFromServerData(data, quoteDetail); // First payment delayed inception date

  appendFirstPaymentDelayedInceptionDate(data, quoteDetail); // Assumption info

  appendAssumptionInfo(data, quoteDetail); // Custom input coverages

  appendCustomInputCoverages(data, quoteDetail); // Align time

  appendTimeFieldsFromServerData(data, quoteDetail);
  return data;
};

export var convertServerQuoteDetailDataToLocalStructureData = function convertServerQuoteDetailDataToLocalStructureData(quoteDetail) {
  var insuranceInfo = getQuoteCurrentInsuranceInfo(quoteDetail);
  var newQuoteDetailData = convertInsuranceServerQuoteDetailDataToLocalStructureData(quoteDetail);

  if (insuranceInfo.isGreateAmerican) {
    convertGreatAmericanSpecialServerQuoteDetailDataToLocalStructureData(newQuoteDetailData, quoteDetail);
  } else if (insuranceInfo.isRecruitment) {
    convertRecruitmentSpecialServerQuoteDetailDataToLocalStructureData(newQuoteDetailData, quoteDetail);
  } else if (insuranceInfo.isGreateAmericanBussinessInsurance) {
    convertGreatAmericanBusinessSpecialServerQuoteDetailDataToLocalStructureData(newQuoteDetailData, quoteDetail);
  } else if (insuranceInfo.isHiscoxUS) {
    convertHiscoxUSSpecialServerQuoteDetailDataToLocalStructureData(newQuoteDetailData, quoteDetail);
  } else if (insuranceInfo.isAccelerant) {
    convertAccelerantSpecialServerQuoteDetailDataToLocalStructureData(newQuoteDetailData, quoteDetail);
  }

  return _objectSpread(_objectSpread(_objectSpread({}, quoteDetail), newQuoteDetailData), insuranceInfo);
};